// src/v3/ui/scheduler/notification-form.tsx

import { useEffect, useState } from "react";
import { Button, Select } from "../_common";
import { NOTIFICATION_CHANNEL_OBJ } from "../_deps";
import { t } from "../locales";
import type {
  NotificationChannel,
  NotificationConfig,
} from "./scheduler.hooks";

const MAX_EMAIL_PORT = 65_535;

interface NotificationFormProps {
  config: NotificationConfig;
  isSaving: boolean;
  isTesting: boolean;
  error: string | null;
  successMessage: string | null;
  onSave: (config: NotificationConfig) => Promise<void>;
  onTest: (config: NotificationConfig) => Promise<void>;
  onClearMessages: () => void;
}

const CHANNEL_OPTIONS: Array<{
  value: NotificationChannel;
  labelKey:
    | "scheduler.notifications.channelNone"
    | "scheduler.notifications.channelWebhook"
    | "scheduler.notifications.channelTelegram"
    | "scheduler.notifications.channelEmail";
}> = [
  {
    value: NOTIFICATION_CHANNEL_OBJ.NONE,
    labelKey: "scheduler.notifications.channelNone",
  },
  {
    value: NOTIFICATION_CHANNEL_OBJ.WEBHOOK,
    labelKey: "scheduler.notifications.channelWebhook",
  },
  {
    value: NOTIFICATION_CHANNEL_OBJ.TELEGRAM,
    labelKey: "scheduler.notifications.channelTelegram",
  },
  {
    value: NOTIFICATION_CHANNEL_OBJ.EMAIL,
    labelKey: "scheduler.notifications.channelEmail",
  },
];

const inputClass =
  "mt-1 block w-full rounded-md border px-3 py-2 text-sm";
const labelClass = "text-xs text-muted-foreground";

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      {children}
    </div>
  );
}

export function NotificationForm({
  config,
  isSaving,
  isTesting,
  error,
  successMessage,
  onSave,
  onTest,
  onClearMessages,
}: NotificationFormProps) {
  const [form, setForm] = useState<NotificationConfig>(config);

  useEffect(() => {
    setForm(config);
  }, [config]);

  const set = <K extends keyof NotificationConfig>(
    key: K,
    value: NotificationConfig[K],
  ) => {
    setForm({ ...form, [key]: value });
    onClearMessages();
  };

  const busy = isSaving || isTesting;

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        {t("scheduler.notifications.hint")}
      </p>

      <div>
        <label className={labelClass}>
          {t("scheduler.notifications.channel")}
        </label>
        <div className="mt-1">
          <Select
            value={form.channel}
            onChange={(v) => {
              const channel = CHANNEL_OPTIONS.find(
                (option) => option.value === v,
              )?.value;
              if (channel) set("channel", channel);
            }}
            options={CHANNEL_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
            disabled={busy}
          />
        </div>
      </div>

      {form.channel === NOTIFICATION_CHANNEL_OBJ.WEBHOOK && (
        <Field label={t("scheduler.notifications.webhookUrl")}>
          <input
            type="text"
            value={form.webhook.url}
            onChange={(e) => {
              set("webhook", { url: e.target.value });
            }}
            placeholder="https://…"
            className={inputClass}
            disabled={busy}
          />
        </Field>
      )}

      {form.channel === NOTIFICATION_CHANNEL_OBJ.TELEGRAM && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Field label={t("scheduler.notifications.botToken")}>
            <input
              type="text"
              value={form.telegram.botToken}
              onChange={(e) => {
                set("telegram", {
                  ...form.telegram,
                  botToken: e.target.value,
                });
              }}
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.chatId")}>
            <input
              type="text"
              value={form.telegram.chatId}
              onChange={(e) => {
                set("telegram", {
                  ...form.telegram,
                  chatId: e.target.value,
                });
              }}
              className={inputClass}
              disabled={busy}
            />
          </Field>
        </div>
      )}

      {form.channel === NOTIFICATION_CHANNEL_OBJ.EMAIL && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Field label={t("scheduler.notifications.smtpHost")}>
            <input
              type="text"
              value={form.email.host}
              onChange={(e) => {
                set("email", { ...form.email, host: e.target.value });
              }}
              placeholder="smtp.example.com"
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.smtpPort")}>
            <input
              type="number"
              min={1}
              max={MAX_EMAIL_PORT}
              value={form.email.port}
              onChange={(e) => {
                set("email", {
                  ...form.email,
                  port: Number(e.target.value) || 465,
                });
              }}
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.smtpUser")}>
            <input
              type="text"
              value={form.email.user}
              onChange={(e) => {
                set("email", { ...form.email, user: e.target.value });
              }}
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.smtpPassword")}>
            <input
              type="password"
              value={form.email.password}
              onChange={(e) => {
                set("email", { ...form.email, password: e.target.value });
              }}
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.emailFrom")}>
            <input
              type="text"
              value={form.email.from}
              onChange={(e) => {
                set("email", { ...form.email, from: e.target.value });
              }}
              placeholder="scheduler@example.com"
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <Field label={t("scheduler.notifications.emailTo")}>
            <input
              type="text"
              value={form.email.to}
              onChange={(e) => {
                set("email", { ...form.email, to: e.target.value });
              }}
              placeholder="alerts@example.com"
              className={inputClass}
              disabled={busy}
            />
          </Field>
          <div className="flex items-center gap-2 sm:col-span-2">
            <input
              id="smtp-secure"
              type="checkbox"
              checked={form.email.secure}
              onChange={(e) => {
                set("email", { ...form.email, secure: e.target.checked });
              }}
              disabled={busy}
            />
            <label htmlFor="smtp-secure" className={labelClass}>
              {t("scheduler.notifications.smtpSecure")}
            </label>
          </div>
        </div>
      )}

      {form.channel !== NOTIFICATION_CHANNEL_OBJ.NONE && (
        <p className="text-xs text-muted-foreground">
          {t("scheduler.notifications.maskedHint")}
        </p>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
      {successMessage && (
        <p className="text-xs text-green-600">{successMessage}</p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={() => void onSave(form)}
          disabled={busy}
        >
          {t("scheduler.notifications.save")}
        </Button>
        {form.channel !== NOTIFICATION_CHANNEL_OBJ.NONE && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => void onTest(form)}
            disabled={busy}
          >
            {t("scheduler.notifications.test")}
          </Button>
        )}
      </div>
    </div>
  );
}
